import { usePermissions } from '@/components/providers/PermissionsContextProvider';
import { NmiTemplateDto, previewNmiTemplate } from '@/components/shared/api/endpoints/nmiTemplates';
import {
    useCreateNmiTemplateMutation,
    useUpdateNmiTemplateMutation,
} from '@/components/shared/api/mutations/nmiTemplates';
import {
    useNmiTemplatePlaceholdersQuery,
    useNmiTemplatesQuery,
} from '@/components/shared/api/queries/nmiTemplates';
import { TemplateManager } from '@/components/shared/components/TemplateManager';
import { Feature } from '@/components/shared/constants/features';

export const NmiTemplatesTab = () => {
    const { hasFeature } = usePermissions();
    const canManage = hasFeature(Feature.ManageTemplates);
    const canEdit = hasFeature(Feature.EditTemplates);

    const { data: nmiTemplates, isLoading, isError, isRefetching, refetch } = useNmiTemplatesQuery();
    const { data: placeholders } = useNmiTemplatePlaceholdersQuery();
    const { mutateAsync: createNmiTemplate } = useCreateNmiTemplateMutation();
    const { mutateAsync: updateNmiTemplate } = useUpdateNmiTemplateMutation();

    const items = nmiTemplates ?? [];

    const setActive = (item: NmiTemplateDto, isActive: boolean) =>
        updateNmiTemplate({
            id: item.id,
            request: { label: item.label, body: item.body, isActive, rowVersion: item.rowVersion },
        });

    return (
        <TemplateManager<NmiTemplateDto>
            title="NMI Template"
            items={items}
            placeholders={() => placeholders ?? []}
            isLoading={isLoading}
            isLoadingError={isError}
            isRefetching={isRefetching}
            onRetry={refetch}
            canManage={canManage}
            canEdit={canEdit}
            onCreate={(values) => createNmiTemplate({ label: values.label, body: values.body })}
            onUpdate={(id, values) => {
                const existing = items.find((item) => item.id === id);
                return updateNmiTemplate({
                    id,
                    request: {
                        label: values.label,
                        body: values.body,
                        isActive: existing?.isActive ?? true,
                        rowVersion: existing?.rowVersion ?? 0,
                    },
                });
            }}
            onRetireItem={(item) => setActive(item, false)}
            onReactivateItem={(item) => setActive(item, true)}
            onPreview={(body) => previewNmiTemplate(body)}
        />
    );
};
